<template>
  <!-- Scoreboard league layout -->
  <SectionRow
    v-if="showTeamLogos"
    row-class="league-score"
    :can-edit="showEdit && canEditEvent"
    :show-menu="showPinnedActions ? canEditEvent : showMenu"
    :hide-actions="hideActions"
    @click="emit('navigate'); router.push(`/gamecenter/${event._id}`)"
    @menu-open="emit('menuOpen')"
    @menu-close="emit('menuClose')"
  >
    <!-- Time column (desktop) -->
    <div class="league-score-time-col">
      <span class="league-score-time-col__time">{{ event.timeFrom || '' }}</span>
      <span v-if="!hasGameResult" class="league-score-time-col__date">{{ dateText }}</span>
      <span v-else class="league-score-time-col__status">{{ $t('eventTile.result') }}</span>
    </div>

    <!-- Home team -->
    <div class="league-score-team">
      <div class="league-score-logo-wrap">
        <img
          v-if="event?.meta?.homeTeam?.imageUrl"
          :src="getTeamImage(event.meta.homeTeam.imageUrl)"
          class="league-score-logo"
        />
        <div v-else class="league-score-logo league-score-logo--placeholder">
          <v-icon size="20" color="rgba(var(--v-theme-sheetText), 0.2)">mdi-shield-outline</v-icon>
        </div>
        <span class="league-score-badge" :class="whiteTeamStatus.color === 'green' ? 'badge-ok' : 'badge-warn'">{{ whiteTeamStatus.resultString }}</span>
      </div>
      <span class="league-score-team-name">{{ homeTeamName }}</span>
    </div>

    <!-- Home goals (if result exists) -->
    <span v-if="hasGameResult" class="league-score-goals" :class="{ 'league-score-goals--winner': homeGoals > awayGoals }">{{ homeGoals }}</span>

    <!-- Center: VS / status / date+time (mobile fallback) -->
    <div class="league-score-center">
      <span v-if="showLeagueHeader" class="league-score-league">{{ event._league?.title || event.title || $t('eventTile.leagueFallback') }}</span>
      <span class="league-score-vs">VS</span>
      <span v-if="overtimeLabel" class="league-score-ot-chip">{{ overtimeLabel }}</span>
      <span v-if="!hasGameResult" class="league-score-date league-score-date--mobile" :class="{ 'league-score-date--small': showLeagueHeader }">{{ dateText }}</span>
      <span class="league-score-time league-score-time--mobile">{{ event.timeFrom || '' }}</span>
      <span v-if="event.title" class="league-score-event-title">{{ event.title }}</span>
      <span v-if="placeText" class="league-score-place">{{ placeText }}</span>
    </div>

    <!-- Away goals (if result exists) -->
    <span v-if="hasGameResult" class="league-score-goals" :class="{ 'league-score-goals--winner': awayGoals > homeGoals }">{{ awayGoals }}</span>

    <!-- Away team -->
    <div class="league-score-team">
      <div class="league-score-logo-wrap">
        <img
          v-if="event?.meta?.awayTeam?.imageUrl"
          :src="getTeamImage(event.meta.awayTeam.imageUrl)"
          class="league-score-logo"
        />
        <div v-else class="league-score-logo league-score-logo--placeholder">
          <v-icon size="20" color="rgba(var(--v-theme-sheetText), 0.2)">mdi-shield-outline</v-icon>
        </div>
        <span class="league-score-badge" :class="blueTeamStatus.color === 'green' ? 'badge-ok' : 'badge-warn'">{{ blueTeamStatus.resultString }}</span>
      </div>
      <span class="league-score-team-name">{{ awayTeamName }}</span>
    </div>

    <span v-if="isCanceled" class="league-score-canceled-mask"></span>
    <span v-if="isCanceled" class="league-score-canceled-stamp">{{ canceledStampLabel }}</span>

    <!-- Context menu items -->
    <template #menu="{ close }">
      <div class="context-menu-item" @click="router.push(`/gamecenter/${event._id}`); close()">
        <span>{{ $t('eventTile.gamecenter') }}</span>
        <v-icon size="18">mdi-chevron-right</v-icon>
      </div>
      <template v-if="canAccessDataLog">
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="router.push(`/eventdatalog/${event._id}`); close()">
          <span>{{ $t('eventTile.gameLog') }}</span>
          <v-icon size="18">mdi-chevron-right</v-icon>
        </div>
      </template>
      <template v-if="hasVideo && canManageVideo">
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="recapRef?.openPlayer(); close()">
          <span>Highlights</span>
          <v-icon size="18">mdi-play-circle-outline</v-icon>
        </div>
      </template>
      <template v-else-if="canManageVideo">
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="recapRef?.openAdd(); close()">
          <span>{{ $t('eventTile.addHighlights') }}</span>
          <v-icon size="18">mdi-plus-circle-outline</v-icon>
        </div>
      </template>
      <template v-if="canEditEvent">
        <div class="context-menu-divider"></div>
        <template v-if="showPinnedActions">
          <div class="context-menu-item" @click="emit('unpinEvent', event); close()">
            <span>Odepnout</span>
            <v-icon size="18">mdi-pin-off-outline</v-icon>
          </div>
          <div class="context-menu-divider"></div>
          <div class="context-menu-item" @click="emit('editEvent', event); close()">
            <span>{{ $t('eventTile.edit') }}</span>
            <v-icon size="18">mdi-pencil-outline</v-icon>
          </div>
          <div class="context-menu-divider"></div>
          <div class="context-menu-item context-menu-item--danger" @click="emit('cancelEvent', event); close()">
            <span>Zrušit</span>
            <v-icon size="18">mdi-close-circle-outline</v-icon>
          </div>
        </template>
        <template v-else>
          <div class="context-menu-item" @click="emit('editEvent', event); close()">
            <span>{{ $t('eventTile.edit') }}</span>
            <v-icon size="18">mdi-pencil-outline</v-icon>
          </div>
          <div class="context-menu-divider"></div>
          <div class="context-menu-item context-menu-item--danger" @click="emit('deleteEvent', event); close()">
            <span>{{ $t('eventTile.deleteEvent') }}</span>
            <v-icon size="18">mdi-trash-can-outline</v-icon>
          </div>
        </template>
      </template>
    </template>
  </SectionRow>

  <!-- Default layout (custom sections, etc.) -->
  <EventTileBase
    v-else
    :event="event"
    :showEdit="showEdit"
    :canEdit="canEditEvent"
    :showMenu="showMenu"
    :showPinnedActions="showPinnedActions"
    :showLeagueLogo="showLeagueLogo"
    :hideActions="hideActions"
    @navigate="emit('navigate'); router.push(`/gamecenter/${event._id}`)"
    @unpinEvent="emit('unpinEvent', $event)"
    @deleteEvent="emit('deleteEvent', $event)"
    @cancelEvent="emit('cancelEvent', $event)"
    @editEvent="emit('editEvent', $event)"
    @menuOpen="emit('menuOpen')"
    @menuClose="emit('menuClose')"
  >
    <template #detail>
      <div class="match-tile-teams">
        <span class="match-tile-team">
          <img v-if="event?.meta?.homeTeam?.imageUrl" :src="getTeamImage(event.meta.homeTeam.imageUrl)" class="match-tile-team-logo" />
          <span :style="{ color: whiteTeamStatus.color === 'green' ? '#4caf50' : '#ef5350' }">{{ whiteTeamStatus.resultString }}</span>
        </span>
        <span class="match-tile-vs">vs</span>
        <span class="match-tile-team">
          <img v-if="event?.meta?.awayTeam?.imageUrl" :src="getTeamImage(event.meta.awayTeam.imageUrl)" class="match-tile-team-logo" />
          <span :style="{ color: blueTeamStatus.color === 'green' ? '#4caf50' : '#ef5350' }">{{ blueTeamStatus.resultString }}</span>
        </span>
      </div>
    </template>
  </EventTileBase>

  <!-- Hidden EventRecap for video player / add video dialog -->
  <EventRecap
    ref="recapRef"
    :event="event"
    :is-league-admin="canManageVideo"
    simple
    style="display: none"
  />
</template>

<script setup lang="ts">
import { computed, toRef, getCurrentInstance, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { IEvent } from '@/types'
import EventTileBase from '@/components/EventTiles/EventTileBase.vue'
import SectionRow from '@/components/SectionRow.vue'
import EventRecap from '@/components/EventRecap.vue'
import { useEventPermissions } from '@/composables/useEventPermissions'
import { useMatchTeamStatus } from '@/composables/useMatchTeamStatus'
import { getTeamImage } from '@/utils/imageUtils'
import { removeTrailingNumbers } from '@/utils/teamUtils'
import { useUserStore } from '@/stores/user/userStore'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })

const props = defineProps<{
  event: IEvent
  fullDate?: boolean
  showEdit: boolean
  showMenu?: boolean
  showPinnedActions?: boolean
  showLeagueLogo?: boolean
  showTeamLogos?: boolean
  showLeagueHeader?: boolean
  hideActions?: boolean
}>()

const router = useRouter()
const emit = defineEmits(['unpinEvent', 'deleteEvent', 'cancelEvent', 'editEvent', 'menuOpen', 'menuClose', 'navigate'])

// EventRecap ref for triggering video player from context menu
const recapRef = ref<InstanceType<typeof EventRecap> | null>(null)
const hasVideo = computed(() => !!props.event?.eventVideo)


const userStore = useUserStore()

const { canEditEvent } = useEventPermissions(toRef(props, 'event'), 'league-admin')
const { whiteTeamStatus, blueTeamStatus } = useMatchTeamStatus(toRef(props, 'event'))

/** Normalize populated ID array */
const norm = (arr: any) =>
  (Array.isArray(arr)
    ? arr.map((x: any) => typeof x === 'string' ? x : (x?._id ?? x?.id ?? String(x))).filter(Boolean)
    : []) as string[]

/** User is league admin or league timekeeper */
const canManageVideo = computed(() => {
  if (canEditEvent.value) return true
  const uid = (userStore.user?._id as string) || ''
  const lg: any = (props.event as any)?._league
  if (!uid || !lg) return false
  return norm(lg.timekeepers).includes(uid) || norm(lg.admins).includes(uid)
})

/** User can access data log (league admin, league timekeeper, coach, administrator) */
const canAccessDataLog = computed(() => {
  if (canManageVideo.value) return true
  const roles = userStore.user?.userRoles || []
  return roles.includes('administrator') || roles.includes('coach')
})

// Date text for compact layout
const instance = getCurrentInstance()
const $formatDate = instance?.appContext.config.globalProperties.$formatDate

const dateText = computed(() => {
  if (!props.event.dateFrom) return ''
  const from = (props.event as any).dateFromRaw ?? props.event.dateFrom
  const to = (props.event as any).dateToRaw ?? props.event.dateTo
  return $formatDate ? $formatDate(from, to) : String(from)
})

const placeText = computed(() => {
  return props.event.place || props.event.location || props.event.meta?.place || ''
})

// Check if game has final result
const hasGameResult = computed(() => {
  const gs = props.event.gameStatus as any
  return gs?.home?.goals !== undefined && gs?.away?.goals !== undefined
})

const homeGoals = computed(() => (props.event.gameStatus as any)?.home?.goals ?? 0)
const awayGoals = computed(() => (props.event.gameStatus as any)?.away?.goals ?? 0)

const overtimeLabel = computed(() => {
  const gs = props.event.gameStatus as any
  if (!gs || !hasGameResult.value) return ''
  if (gs.shootouts) return t('chipSN')
  if (gs.overtime) return t('chipOT')
  return ''
})

const homeTeamName = computed(() => {
  const ev = props.event as any
  const ht = ev.meta?.homeTeam
  if (ht?.shortName) return ht.shortName
  if (ht?.name) return removeTrailingNumbers(ht.name)
  if (ev.playoffMeta?.groupSlotHomeLabel) return ev.playoffMeta.groupSlotHomeLabel
  return t('eventTile.homeTeam')
})

const awayTeamName = computed(() => {
  const ev = props.event as any
  const at = ev.meta?.awayTeam
  if (at?.shortName) return at.shortName
  if (at?.name) return removeTrailingNumbers(at.name)
  if (ev.playoffMeta?.groupSlotAwayLabel) return ev.playoffMeta.groupSlotAwayLabel
  return t('eventTile.awayTeam')
})

const isCanceled = computed(() => Boolean((props.event as any)?.canceled))
const canceledStampLabel = computed(() => {
  const key = 'eventTile.canceledStamp'
  const label = t(key)
  return label === key ? 'CANCELED' : label
})
</script>

<style lang="scss" scoped>
/* ── Scoreboard league layout (deep for SectionRow child) ── */
:deep(.league-score) {
  display: flex;
  align-items: center;
  padding: 10px 10px;
  gap: 4px;
  cursor: pointer;
  position: relative;
}

:deep(.league-score)::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 16px;
  right: 16px;
  height: 1px;
  background: linear-gradient(90deg,
      transparent 0%,
      rgba(var(--v-theme-sheetText), 0.12) 15%,
      rgba(var(--v-theme-sheetText), 0.12) 85%,
      transparent 100%);
}

:deep(.league-score):active {
  background: rgba(var(--v-theme-sheetText), 0.04);
}

:deep(.league-score-canceled-mask) {
  position: absolute;
  inset: 0;
  z-index: 2;
  background:
    linear-gradient(180deg, rgba(7, 10, 16, 0.16) 0%, rgba(7, 10, 16, 0.3) 100%),
    rgba(12, 16, 24, 0.18);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.04),
    inset 0 0 24px rgba(0, 0, 0, 0.14);
  backdrop-filter: saturate(0.72) blur(1.2px);
  pointer-events: none;
}

:deep(.league-score-canceled-stamp) {
  position: absolute;
  right: 36px;
  top: 10px;
  z-index: 3;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(245, 124, 0, 0.98);
  background: rgba(0, 0, 0, 0.06);
  border: 3px solid rgba(245, 124, 0, 0.95);
  border-radius: 6px;
  box-shadow:
    0 6px 14px rgba(0, 0, 0, 0.35),
    0 1px 0 rgba(255, 255, 255, 0.2) inset,
    0 0 0 1px rgba(245, 124, 0, 0.28) inset;
  text-shadow:
    0 0 1px rgba(245, 124, 0, 0.55),
    0 0 0.5px rgba(245, 124, 0, 0.75);
  padding: 5px 12px 4px;
  transform: rotate(-14deg);
  transform-origin: center;
  opacity: 0.9;
  filter: saturate(0.92) contrast(1.05);
  pointer-events: none;
  backdrop-filter: blur(0.4px);
}

/* ── Time column (desktop only) ── */
:deep(.league-score-time-col) {
  display: none;
}

@media (min-width: 600px) {
  :deep(.league-score-time-col) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 52px;
    flex-shrink: 0;
    gap: 2px;
    padding-right: 8px;
    border-right: 1px solid rgba(var(--v-theme-sheetText), 0.08);
    margin-right: 4px;
  }

  :deep(.league-score-time-col__time) {
    font-size: 0.9rem;
    font-weight: 700;
    color: rgba(var(--v-theme-sheetText), 0.85);
    line-height: 1.15;
  }

  :deep(.league-score-time-col__date) {
    font-size: 0.65rem;
    font-weight: 500;
    color: rgba(var(--v-theme-sheetText), 0.4);
    white-space: nowrap;
  }

  :deep(.league-score-time-col__status) {
    font-size: 0.62rem;
    font-weight: 600;
    color: rgba(var(--v-theme-sheetText), 0.4);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  /* Desktop: hide mobile-only date/time from center */
  :deep(.league-score-date--mobile) {
    display: none;
  }

  :deep(.league-score-time--mobile) {
    display: none;
  }

  /* Desktop: center section compact */
  :deep(.league-score-center) {
    flex: 0 0 auto;
    min-width: 24px;
  }

  /* Desktop: restore original sizes */
  :deep(.league-score) {
    padding: 12px 14px;
    gap: 6px;
  }

  :deep(.league-score-team) {
    width: auto;
    gap: 8px;
  }

  :deep(.league-score-logo) {
    width: 44px;
    height: 44px;
    border-radius: 8px;
  }

  :deep(.league-score-goals) {
    font-size: 2.5rem;
    min-width: 32px;
  }

  /* Desktop: teams compact, horizontal layout */
  :deep(.league-score-team) {
    flex-direction: row;
    width: auto;
    gap: 8px;
  }

  :deep(.league-score-team-name) {
    text-align: left;
    max-width: 120px;
  }

  :deep(.league-score-place) {
    display: block;
  }

  :deep(.league-score-event-title) {
    display: block;
  }
}

:deep(.league-score-team) {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 56px;
  flex-shrink: 0;
}

:deep(.league-score-logo-wrap) {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.league-score-logo) {
  width: 34px;
  height: 34px;
  object-fit: contain;
  border-radius: 6px;
  flex-shrink: 0;
}

:deep(.league-score-logo--placeholder) {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-sheetText), 0.04);
}

:deep(.league-score-badge) {
  position: absolute;
  bottom: -6px;
  right: -10px;
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  padding: 3px 5px;
  border-radius: 7px;
  white-space: nowrap;
  letter-spacing: 0.02em;
  border: 2px solid rgba(var(--v-theme-sheetSection), 1);
}

:deep(.badge-ok) {
  background: #4caf50;
  color: #fff;
}

:deep(.badge-warn) {
  background: #ef5350;
  color: #fff;
}

:deep(.league-score-team-name) {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.6);
  text-align: center;
  line-height: 1.15;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

:deep(.league-score-center) {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
}

/* ── VS text ── */
:deep(.league-score-vs) {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.25);
  letter-spacing: 0.08em;
  line-height: 1;
}

:deep(.league-score-ot-chip) {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.58rem;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.45);
  background: rgba(var(--v-theme-sheetText), 0.08);
  padding: 1px 5px;
  border-radius: 4px;
  letter-spacing: 0.05em;
  line-height: 1.2;
  text-transform: uppercase;
}

:deep(.league-score-league) {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.5);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

:deep(.league-score-date) {
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.7);
}

:deep(.league-score-date--small) {
  font-size: 0.7rem;
  font-weight: 400;
  color: rgba(var(--v-theme-sheetText), 0.45);
}

:deep(.league-score-goals) {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 1.6rem;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.35);
  line-height: 1;
  min-width: 22px;
  text-align: center;
}

:deep(.league-score-goals--winner) {
  color: rgba(var(--v-theme-sheetText), 1);
}

:deep(.league-score-status) {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.5);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

:deep(.league-score-separator) {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 1.2rem;
  font-weight: 400;
  color: rgba(var(--v-theme-sheetText), 0.3);
  line-height: 1;
}

:deep(.league-score-time) {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.7);
}

:deep(.league-score-event-title) {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.5);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

:deep(.league-score-place) {
  font-size: 0.72rem;
  font-weight: 400;
  color: rgba(var(--v-theme-sheetText), 0.35);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  display: none;
}

/* ── Inline highlights / add-highlights button ── */
:deep(.league-score-highlights) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: none;
  border-radius: 14px;
  background: rgba(var(--v-theme-sheetText), 0.06);
  color: rgba(var(--v-theme-sheetText), 0.6);
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease;
}

:deep(.league-score-highlights):active {
  background: rgba(var(--v-theme-sheetText), 0.12);
}

:deep(.league-score-highlights--add) {
  color: rgba(var(--v-theme-sheetText), 0.35);
  background: rgba(var(--v-theme-sheetText), 0.04);
  border: 1px dashed rgba(var(--v-theme-sheetText), 0.15);
}

:deep(.league-score-highlights--add):active {
  background: rgba(var(--v-theme-sheetText), 0.08);
}

:deep(.league-score-chevron) {
  color: rgba(var(--v-theme-sheetText), 0.18);
  flex-shrink: 0;
  margin-left: auto;
}

:deep(.league-score-menu) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  margin-left: auto;
  border: none;
  background: transparent;
  cursor: pointer;
  color: rgba(var(--v-theme-sheetText), 0.4);
  border-radius: 8px;
  transition: background 0.15s ease;
}

:deep(.league-score-menu):active {
  background: rgba(var(--v-theme-sheetText), 0.08);
}

/* ── Default detail layout ── */
.match-tile-teams {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
}

.match-tile-team {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 600;
}

.match-tile-team-logo {
  width: 18px;
  height: 18px;
  object-fit: contain;
  border-radius: 4px;
}

.match-tile-vs {
  font-size: 0.68rem;
  font-weight: 400;
  color: rgba(var(--v-theme-sheetText), 0.35);
  text-transform: uppercase;
}
</style>
